import { supabase } from '../lib/supabase';

/**
 * PAYMENT METHOD CLARIFICATION:
 * 
 * 1. STRIPE PAYMENT (Automated):
 *    - Automated card processing via Stripe API
 *    - Instant payment confirmation
 *    - Automatic invoice generation and email delivery
 *    - Real-time payment status updates
 *    - Stored payment methods for recurring billing
 * 
 * 2. DIRECT PAYMENT (Manual):
 *    - Manual bank portal login required (BSP/Kinabank/Credit Bank)
 *    - Customer must email finance team to request invoice
 *    - Manual payment processing through corporate banking portal
 *    - Manual reconciliation required
 *    - No automated invoice generation
 *    - Payment confirmation via bank statement
 */

export const billingService = {
  async getInvoiceHistory(companyId) {
    const { data: subscription } = await supabase
      ?.from('company_subscriptions')
      ?.select('id')
      ?.eq('company_id', companyId)
      ?.maybeSingle();

    if (!subscription) return [];

    const { data, error } = await supabase
      ?.from('subscription_billing_history')
      ?.select('*')
      ?.eq('company_subscription_id', subscription?.id)
      ?.order('billing_date', { ascending: false });

    if (error) throw error;

    return data?.map(invoice => ({
      id: invoice?.id,
      companySubscriptionId: invoice?.company_subscription_id,
      billingPeriodStart: invoice?.billing_period_start,
      billingPeriodEnd: invoice?.billing_period_end,
      tierAtBilling: invoice?.tier_at_billing,
      employeeCountAtBilling: invoice?.employee_count_at_billing,
      amountKina: invoice?.amount_kina,
      billingDate: invoice?.billing_date,
      paymentStatus: this.calculatePaymentStatus(invoice?.billing_date),
      notes: invoice?.notes,
      createdAt: invoice?.created_at
    })) || [];
  },

  calculatePaymentStatus(billingDate) {
    if (!billingDate) return 'pending';
    
    const billDate = new Date(billingDate);
    const today = new Date();
    const daysDiff = Math.floor((today - billDate) / (1000 * 60 * 60 * 24));

    if (daysDiff <= 7) return 'paid';
    if (daysDiff <= 30) return 'pending';
    return 'overdue';
  },

  async getSubscriptionDetails(companyId) {
    const { data, error } = await supabase
      ?.from('company_subscriptions')
      ?.select(`
        *,
        company_settings:company_id (
          company_name,
          company_legal_name
        )
      `)
      ?.eq('company_id', companyId)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      companyId: data?.company_id,
      currentTier: data?.current_tier,
      subscriptionStatus: data?.subscription_status,
      activeEmployeeCount: data?.active_employee_count,
      monthlyPriceKina: data?.monthly_price_kina,
      billingPeriodStart: data?.billing_period_start,
      billingPeriodEnd: data?.billing_period_end,
      nextBillingDate: data?.next_billing_date,
      lastTierChangeDate: data?.last_tier_change_date,
      companyName: data?.company_settings?.company_name,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async getSubscriptionTiers(organizationType = 'private') {
    const { data, error } = await supabase
      ?.from('subscription_tiers')
      ?.select('*')
      ?.order('min_employees', { ascending: true });

    if (error) throw error;

    return data?.map(tier => ({
      id: tier?.id,
      tierName: tier?.tier_name,
      displayName: tier?.display_name,
      minEmployees: tier?.min_employees,
      maxEmployees: tier?.max_employees,
      monthlyPriceKinaMin: tier?.monthly_price_kina_min,
      monthlyPriceKinaMax: tier?.monthly_price_kina_max,
      description: tier?.description,
      features: tier?.features,
      targetUsers: tier?.target_users,
      valueProposition: tier?.value_proposition,
      createdAt: tier?.created_at
    }));
  },

  async getPaymentStatistics(companyId) {
    const invoices = await this.getInvoiceHistory(companyId);
    
    const totalInvoices = invoices?.length || 0;
    const paidInvoices = invoices?.filter(inv => inv?.paymentStatus === 'paid')?.length || 0;
    const pendingInvoices = invoices?.filter(inv => inv?.paymentStatus === 'pending')?.length || 0;
    const overdueInvoices = invoices?.filter(inv => inv?.paymentStatus === 'overdue')?.length || 0;
    const totalSpent = invoices?.reduce((sum, inv) => sum + (parseFloat(inv?.amountKina) || 0), 0);

    return {
      totalInvoices,
      paidInvoices,
      pendingInvoices,
      overdueInvoices,
      totalSpent,
      averageMonthlySpend: totalInvoices > 0 ? totalSpent / totalInvoices : 0
    };
  },

  async getActiveCompany() {
    const { data, error } = await supabase
      ?.from('company_settings')
      ?.select('*')
      ?.eq('is_active', true)
      ?.maybeSingle();

    if (error) throw error;
    if (!data) {
      throw new Error('No active company found. Please activate a company in settings.');
    }

    return {
      id: data?.id,
      companyName: data?.company_name,
      companyLegalName: data?.company_legal_name,
      contactEmail: data?.contact_email,
      contactPhone: data?.contact_phone,
      companyAddress: data?.company_address,
      taxIdentificationNumber: data?.tax_identification_number,
      businessRegistrationNumber: data?.business_registration_number
    };
  },

  formatTierName(tierName) {
    const tierMap = {
      'basic': 'Basic Tier',
      'standard': 'Standard Tier',
      'premium': 'Premium Tier',
      'enterprise': 'Public Sector'
    };
    return tierMap?.[tierName] || tierName;
  },

  calculateDaysUntilBilling(nextBillingDate) {
    if (!nextBillingDate) return null;
    
    const nextDate = new Date(nextBillingDate);
    const today = new Date();
    const daysDiff = Math.ceil((nextDate - today) / (1000 * 60 * 60 * 24));
    
    return daysDiff > 0 ? daysDiff : 0;
  },

  /**
   * Automatically create invoice for billing period
   * Called when billing tier changes or new billing period starts
   */
  async createAutomaticInvoice(companyId, billingHistoryId) {
    try {
      // Call Supabase Edge Function to create Stripe invoice
      const { data: { session } } = await supabase?.auth?.getSession();
      
      const response = await fetch(
        `${supabase?.supabaseUrl}/functions/v1/create-subscription-invoice`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            billingHistoryId,
            companyId
          })
        }
      );

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.error || 'Failed to create invoice');
      }

      const result = await response?.json();
      return result;
    } catch (error) {
      console.error('Error creating automatic invoice:', error);
      throw error;
    }
  },

  /**
   * Check for pending billing periods and create invoices
   * Should be called periodically or when subscription changes
   */
  async processPendingInvoices(companyId) {
    try {
      // Get subscription
      const { data: subscription } = await supabase
        ?.from('company_subscriptions')
        ?.select('id')
        ?.eq('company_id', companyId)
        ?.single();

      if (!subscription) {
        return { message: 'No active subscription found' };
      }

      // Get billing history without invoices
      const { data: pendingBilling, error } = await supabase
        ?.from('subscription_billing_history')
        ?.select('id')
        ?.eq('company_subscription_id', subscription?.id)
        ?.is('stripe_invoice_id', null)
        ?.order('billing_date', { ascending: false });

      if (error) throw error;

      if (!pendingBilling || pendingBilling?.length === 0) {
        return { message: 'No pending invoices to process' };
      }

      // Create invoices for each pending billing period
      const results = [];
      for (const billing of pendingBilling) {
        try {
          const invoice = await this.createAutomaticInvoice(companyId, billing?.id);
          results?.push({ billingId: billing?.id, success: true, invoice });
        } catch (err) {
          results?.push({ billingId: billing?.id, success: false, error: err?.message });
        }
      }

      return {
        processed: results?.length,
        successful: results?.filter(r => r?.success)?.length,
        failed: results?.filter(r => !r?.success)?.length,
        details: results
      };
    } catch (error) {
      console.error('Error processing pending invoices:', error);
      throw error;
    }
  },

  /**
   * Get company logo URL for invoice branding
   */
  async getCompanyBranding(companyId) {
    try {
      const { data, error } = await supabase
        ?.from('company_settings')
        ?.select('company_logo_url, company_name, company_legal_name')
        ?.eq('id', companyId)
        ?.single();

      if (error) throw error;

      return {
        logoUrl: data?.company_logo_url || '/assets/images/RBS_LOGO_23_2_-1768354637228.png',
        companyName: data?.company_name || 'Pacific RBS Ltd',
        companyLegalName: data?.company_legal_name || 'Pacific RBS Limited'
      };
    } catch (error) {
      console.error('Error fetching company branding:', error);
      return {
        logoUrl: '/assets/images/RBS_LOGO_23_2_-1768354637228.png',
        companyName: 'Pacific RBS Ltd',
        companyLegalName: 'Pacific RBS Limited'
      };
    }
  }
};

export default billingService;